import {
  Card,
  CardContent,
  Chip,
  IconButton,
  Link,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {useTranslation} from 'react-i18next';
import moment from 'moment';
import {ReminderRecurrenceType} from '../../backend/interfaces/reminder-recurrence-type';
import {ReminderResponseType} from '../../backend/enums/reminder-response-type';
import {Reminder} from '../../backend/interfaces/reminder';
import {translations} from '../../i18n/translations';

export const ReminderCard = ({
  reminder,
  activeUpdatePending,
  canManage = true,
  onEdit,
  onDelete,
  onToggleActive,
}: ReminderCardProps) => {
  const {t} = useTranslation();

  const recurrenceLabel = (() => {
    switch (reminder.recurrenceType) {
      case ReminderRecurrenceType.Once:
        return t(translations.reminders.recurrence.once);
      case ReminderRecurrenceType.PerMinute:
        return t(translations.reminders.recurrence.per_minute);
      case ReminderRecurrenceType.Hourly:
        return t(translations.reminders.recurrence.hourly, {count: reminder.recurrenceCount});
      case ReminderRecurrenceType.Daily:
        return t(translations.reminders.recurrence.daily, {count: reminder.recurrenceCount});
      case ReminderRecurrenceType.Weekly:
        return t(translations.reminders.recurrence.weekly, {count: reminder.recurrenceCount});
      case ReminderRecurrenceType.Monthly:
        return t(translations.reminders.recurrence.monthly, {count: reminder.recurrenceCount});
      case ReminderRecurrenceType.Yearly:
        return t(translations.reminders.recurrence.yearly, {count: reminder.recurrenceCount});
      default:
        return t(translations.reminders.recurrence.current_unsupported, {
          value: reminder.recurrenceType,
        });
    }
  })();
  const nextRunAt = reminder.nextRunAt || reminder.startAt;
  const responseLabel =
    reminder.responseType === ReminderResponseType.YesOrNo
      ? t(translations.reminders.response_types.yes_no)
      : reminder.responseType === ReminderResponseType.OK
        ? t(translations.reminders.response_types.ok)
        : t(translations.reminders.recurrence.current_unsupported, {
            value: reminder.responseType,
          });

  return (
    <Card variant="outlined" sx={{width: '100%', mb: 2, borderRadius: 2}}>
      <CardContent sx={{'&:last-child': {pb: 2}}}>
        <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} justifyContent="space-between">
          <Stack spacing={0.75} sx={{minWidth: 0, flex: 1}}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="h6" component="h2" sx={{overflowWrap: 'anywhere'}}>
                {canManage ? (
                  <Link
                    component="button"
                    type="button"
                    color="text.primary"
                    underline="hover"
                    disabled={activeUpdatePending}
                    onClick={(event) => {
                      const selection = window.getSelection();
                      if (
                        event.detail !== 0 &&
                        selection &&
                        !selection.isCollapsed &&
                        selection.containsNode(event.currentTarget, true)
                      ) {
                        return;
                      }
                      onEdit(reminder);
                    }}
                    sx={{
                      font: 'inherit',
                      userSelect: 'text',
                      textAlign: 'left',
                      overflowWrap: 'anywhere',
                      '&:disabled': {color: 'text.disabled', cursor: 'default'},
                    }}
                  >
                    {reminder.title}
                  </Link>
                ) : (
                  reminder.title
                )}
              </Typography>
              <Chip
                size="small"
                color={reminder.isActive ? 'success' : 'default'}
                label={
                  reminder.isActive
                    ? t(translations.reminders.status_active)
                    : t(translations.reminders.status_inactive)
                }
              />
            </Stack>
            <Typography variant="body1" sx={{whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>
              {reminder.message}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {recurrenceLabel} · {responseLabel}
            </Typography>
            {reminder.isActive && (
              <Typography variant="body2" color="text.secondary">
                {t(translations.reminders.next_run)}: {moment(nextRunAt).format('LLLL')}
              </Typography>
            )}
          </Stack>
          {canManage && (
            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
              <Tooltip
                title={
                  reminder.isActive
                    ? t(translations.reminders.disable)
                    : t(translations.reminders.enable)
                }
              >
                <Switch
                  checked={reminder.isActive}
                  disabled={activeUpdatePending}
                  inputProps={{
                    'aria-label': reminder.isActive
                      ? t(translations.reminders.disable)
                      : t(translations.reminders.enable),
                  }}
                  onChange={(_, checked) => onToggleActive(reminder, checked)}
                />
              </Tooltip>
              <Tooltip title={t(translations.reminders.edit)}>
                <IconButton
                  aria-label={t(translations.reminders.edit)}
                  disabled={activeUpdatePending}
                  onClick={() => onEdit(reminder)}
                >
                  <EditOutlinedIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t(translations.reminders.delete)}>
                <IconButton
                  aria-label={t(translations.reminders.delete)}
                  color="error"
                  disabled={activeUpdatePending}
                  onClick={() => onDelete(reminder)}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

type IdentifiedReminder = Reminder & {id: string};

interface ReminderCardProps {
  reminder: IdentifiedReminder;
  activeUpdatePending: boolean;
  canManage?: boolean;
  onEdit: (reminder: IdentifiedReminder) => void;
  onDelete: (reminder: IdentifiedReminder) => void;
  onToggleActive: (reminder: IdentifiedReminder, isActive: boolean) => void;
}
